<script setup lang="ts">
import { HitStatus, type MeasureBound, type Note, type PieceSection } from '@/types';
import { gsap } from 'gsap';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useInjectGameEngine } from '../composables/useGameEngine';
import { useInjectSheetMusic } from '../composables/useSheetMusic';
import { useTimeScrubDrag } from '../composables/useTimeScrubDrag';
import { useSettings } from '../composables/useSettings';
import { useGameChromeVisibility } from '../composables/useGameChromeVisibility';
import { useInjectNoteAnnotationsOptional } from '../composables/useNoteAnnotations';
import { MULTI_HUD_VISUAL_FADE_PX, useMultiRowSheetMusic } from '../composables/useMultiRowSheetMusic';
import {
  computeOsmdMeasureBounds,
  computeOsmdNoteCoordinates,
  computeSheetScrollPosition,
} from '@/utils';
import SheetMusicSectionsOverlay from './SheetMusicSectionsOverlay.vue';
import SheetMusicAnnotationsOverlay from './SheetMusicAnnotationsOverlay.vue';
import ToolbarIconToggle from '@/components/shared/ToolbarIconToggle.vue';

const props = withDefaults(defineProps<{
  musicXmlFile?: File
  sections?: PieceSection[]
  canSeek?: boolean
  audioPending?: boolean
}>(), {
  canSeek: true,
  audioPending: false,
});

const emit = defineEmits<{
  'seek': [data: { timeMs: number; wasPlaying: boolean }];
}>();

const sheetViewportEl = ref<HTMLElement>();
const sheetMusicDiv = ref<HTMLElement>();
const targetLine = ref<HTMLElement>();
const scrollReady = ref(false);
const sheetMusic = useInjectSheetMusic();
const gameEngine = useInjectGameEngine();
const scrollOffset = ref(0);
const initialScrollOffset = ref(0);
const targetPosition = ref(200);
const measureBoundsForScroll = ref<MeasureBound[]>([]);
const xmlContentLoaded = ref(false);
const chrome = useGameChromeVisibility();
const noteAnnotations = useInjectNoteAnnotationsOptional();
const settings = useSettings();
const { zoom } = settings;
const styles = getComputedStyle(document.documentElement);
const primaryColor = styles.getPropertyValue('--color-primary').trim();
const perfectParticleColors = [
  styles.getPropertyValue('--color-particle-perfect-1').trim(),
  styles.getPropertyValue('--color-particle-perfect-2').trim(),
  styles.getPropertyValue('--color-particle-perfect-3').trim(),
];

const isSheetLoading = computed(() => !!sheetMusic.isLoading?.value);
const isAudioLoading = computed(() => props.audioPending || !!gameEngine.audioWaveform.value?.isLoading);
const showLoadingOverlay = computed(() => isSheetLoading.value || isAudioLoading.value);
const loadingLabel = computed(() => (isSheetLoading.value ? 'Loading sheet music…' : 'Loading audio…'));
const singleScale = computed(() => Number(zoom.value) || 1);
// Same zoom setting drives OSMD's native re-render zoom in multi-line mode —
// kept as a separate alias only because the two call sites mean different
// things (CSS transform scale vs. OSMD's own `zoom` option).
const multiScale = singleScale;
const activeRenderedNotes = computed(() => gameEngine.currentGameNotes.value.length
  ? gameEngine.currentGameNotes.value
  : sheetMusic.notes.value);

const {
  isMultiLineMode,
  isSwitchingRenderMode,
  modeButtonLabel,
  modeIconName,
  multiSpacerPx,
  multiMarkerStyle,
  multiMarkerArrowTopStyle,
  multiMarkerArrowBottomStyle,
  showMultiMarker,
  handleMultiPointerDown,
  handleMultiPointerUp,
  handleMultiWheel,
  handleMultiScroll,
  handleMultiMeasureClick,
  toggleRenderMode,
  commitMultiSpacer,
  recenterAfterLoad,
} = useMultiRowSheetMusic({
  sheetViewportEl,
  measureBoundsForScroll,
  activeRenderedNotes: () => activeRenderedNotes.value,
  multiScale: () => multiScale.value,
  canSeek: () => props.canSeek,
  onSeek: (data) => emit('seek', data),
  // Wrapped rather than passed directly: both are declared further down this
  // file, but only ever invoked later (never during this composable's own
  // setup), so the indirection just defers the lookup past that point.
  refreshSheetGeometry: () => refreshSheetGeometry(),
  syncRenderedNotesIntoGameNotes: () => syncRenderedNotesIntoGameNotes(),
});

watch(() => props.musicXmlFile, async (file) => {
  if (file && sheetMusicDiv.value) {
    xmlContentLoaded.value = true;
    await loadSheetMusic(file);
  }
}, { immediate: true });

onMounted(async () => {
  if (sheetMusicDiv.value && props.musicXmlFile && !xmlContentLoaded.value) {
    xmlContentLoaded.value = true;
    await loadSheetMusic(props.musicXmlFile);
  }
});

const updateScrollPosition = () => {
  if (isMultiLineMode.value) return;
  const position = computeSheetScrollPosition(
    gameEngine.currentGameNotes.value,
    gameEngine.currentTime.value,
    targetPosition.value,
    initialScrollOffset.value
  );
  if (position) scrollOffset.value = position.scrollOffset;
};

const { start: startSheetDrag } = useTimeScrubDrag({
  notes: () => gameEngine.currentGameNotes.value,
  durationMs: () => sheetMusic.songDuration.value,
  scale: () => singleScale.value,
  enabled: () => !isMultiLineMode.value && props.canSeek && sheetMusic.isLoaded.value,
  onScrub: (timeMs, wasPlaying) => emit('seek', { timeMs, wasPlaying }),
  onTap: () => chrome.handleTap(),
});

// `useHorizontalDrag.start` expects to be bound with the `.prevent` modifier
// (it suppresses text selection/page scroll mid-drag) — but Vue's `.prevent`
// always calls `preventDefault()` on the raw event, regardless of what the
// inline handler expression evaluates to. Gating the call with a plain
// `!isMultiLineMode && …ternary` inside the template still let `.prevent`
// fire unconditionally on every touchstart, including in multi-line mode —
// and preventDefault on `touchstart` is exactly what stops the browser from
// synthesizing the `click` that multi-line's tap-to-seek relies on. Wrapping
// the decision in a real function keeps `preventDefault()` scoped to the
// gesture that actually needs it.
const handleSheetPressStart = (event: MouseEvent | TouchEvent) => {
  if (isMultiLineMode.value || !props.canSeek) return;
  event.preventDefault();
  startSheetDrag(event);
};

// A view-only participant (`!props.canSeek`, e.g. a classroom student) must
// never be able to scroll themselves away from the host's position in
// multi-line mode — not even a "read ahead" allowance. Two layers enforce
// that, both driven off the same condition:
// - `overflow-hidden` strips the scrollbar and blocks user-driven wheel/touch
//   scrolling, while still letting auto-follow drive scrollTop/scrollTo
//   programmatically.
// - `touch-action: none` is belt-and-suspenders: some WebKit builds still let
//   a touchmove rubber-band/scroll an `overflow: hidden` ancestor before any
//   JS handler runs, so this tells the touch compositor up front never to
//   hand this element's gestures to scrolling at all.
const isMultiLineViewOnly = computed(() => isMultiLineMode.value && !props.canSeek);
const sheetViewportOverflowClass = computed(() => (
  isMultiLineMode.value && !isMultiLineViewOnly.value ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'
));
const sheetViewportTouchAction = computed(() => (isMultiLineViewOnly.value ? 'none' : 'auto'));

// Stripped from prod builds (dead code, not just hidden) since import.meta.env.DEV
// is statically replaced/tree-shaken by Vite.
const isDev = import.meta.env.DEV;

// DEBUG: visualizes computeOsmdMeasureBounds' row bands to diagnose
// overlap/sizing — remove once the multi-line hit-test bounds are fixed.
const debugBoundStyle = (bound: MeasureBound, index: number) => {
  const hue = (index * 67) % 360;
  const verticalOffset = isMultiLineMode.value ? multiSpacerPx.value : 0;
  return {
    left: `${bound.x}px`,
    top: `${(bound.y ?? 0) + verticalOffset}px`,
    width: `${bound.width}px`,
    height: `${bound.height ?? 0}px`,
    backgroundColor: `hsl(${hue}, 80%, 55%)`,
    border: `1px solid hsl(${hue}, 80%, 35%)`,
    opacity: 0.35,
  };
};

const resetTargetLineStyles = () => {
  if (!targetLine.value) return;
  targetLine.value.style.background = '';
  targetLine.value.style.borderColor = '';
};

const repaintCurrentNoteheads = () => {
  const groups = new Map<unknown, Note[]>();
  activeRenderedNotes.value.forEach((note) => {
    if (!note.graphicalNote) return;
    const group = groups.get(note.graphicalNote);
    if (group) group.push(note);
    else groups.set(note.graphicalNote, [note]);
  });
  groups.forEach((group) => sheetMusic.colorNoteheadByStatus(group));
};

const syncRenderedNotesIntoGameNotes = () => {
  const renderedNotes = sheetMusic.notes.value;
  const currentNotes = gameEngine.currentGameNotes.value;
  if (!renderedNotes.length || !currentNotes.length) return;

  if (renderedNotes.length !== currentNotes.length) {
    gameEngine.currentGameNotes.value = renderedNotes.map((note, index) => ({
      ...note,
      played: currentNotes[index]?.played ?? false,
      hitStatus: currentNotes[index]?.hitStatus,
      timingOffset: currentNotes[index]?.timingOffset,
    }));
    repaintCurrentNoteheads();
    return;
  }

  renderedNotes.forEach((renderedNote, index) => {
    const currentNote = currentNotes[index];
    currentNote.measureIndex = renderedNote.measureIndex;
    currentNote.graphicalNote = renderedNote.graphicalNote;
    currentNote.noteName = renderedNote.noteName;
    currentNote.possibleMidiNumbers = renderedNote.possibleMidiNumbers;
    currentNote.x = renderedNote.x;
    currentNote.y = renderedNote.y;
    currentNote.isTremoloRepeat = renderedNote.isTremoloRepeat;
    currentNote.decoration = renderedNote.decoration;
  });

  repaintCurrentNoteheads();
};

const recomputeMeasureBounds = () => {
  if (!sheetViewportEl.value || !sheetMusicDiv.value || !sheetMusic.osmd.value) {
    measureBoundsForScroll.value = [];
    return;
  }

  measureBoundsForScroll.value = isMultiLineMode.value
    ? computeOsmdMeasureBounds({
      osmd: sheetMusic.osmd.value,
      osmdEl: sheetMusicDiv.value,
      includeVertical: true,
      includeRowGrouping: true,
    })
    : computeOsmdMeasureBounds({
      osmd: sheetMusic.osmd.value,
      osmdEl: sheetMusicDiv.value,
      includeVertical: true,
    });
};

const refreshSheetGeometry = async () => {
  if (!sheetMusicDiv.value) return;

  const notes = activeRenderedNotes.value;
  if (!notes.length) {
    recomputeMeasureBounds();
    scrollReady.value = true;
    resetTargetLineStyles();
    return;
  }

  scrollOffset.value = 0;
  sheetMusic.showAllMeasures();
  await nextTick();
  computeOsmdNoteCoordinates(notes, sheetMusicDiv.value, isMultiLineMode.value ? 1 : singleScale.value);

  if (!isMultiLineMode.value) {
    initialScrollOffset.value = sheetMusic.calculateInitialScrollOffset(notes);
    updateScrollPosition();
    sheetMusic.updateMeasureVisibility();
  }

  scrollReady.value = true;
  resetTargetLineStyles();

  // The spacer pushes the notation down, so it must have its final size
  // before the bounds are measured. No-op in single-line mode.
  await commitMultiSpacer();
  recomputeMeasureBounds();

  // Every geometry refresh is a fresh layout, so re-establish auto-follow and
  // centre whichever row is current — this is what makes the very first load
  // (not just later row-to-row jumps) land vertically centred instead of
  // pinned to the top. Centralised here (rather than repeated by each caller)
  // so no refresh path can forget it. No-op in single-line mode.
  recenterAfterLoad();
};

const loadSheetMusic = async (file: File) => {
  if (!sheetMusicDiv.value) return;
  scrollReady.value = false;

  await sheetMusic.initOSMD(
    sheetMusicDiv.value,
    { renderSingleHorizontalStaffline: !isMultiLineMode.value },
  );

  await sheetMusic.handleFileUpload(file);
  await sheetMusic.loadMusicXML(isMultiLineMode.value ? multiScale.value : 1);
  await refreshSheetGeometry();
};

watch(() => gameEngine.currentGameNotes.value, async () => {
  if (!sheetMusicDiv.value || !sheetMusic.isLoaded.value) return;
  await refreshSheetGeometry();
});

watch(gameEngine.currentTime, () => {
  updateScrollPosition();
});

const fireworks = ref<Array<{ id: number; class: string }>>([]);
const nextFireworkId = 0;
const fireworkAnimationDuration = 800;
let activeHitTextTimeout: number | null = null;
const HIT_TEXT_BASE_CLASS = 'hit-text-container top-6 left-70';

const showHitText = (text: string, textClass: string) => {
  const container = document.getElementById('hit-text-container');
  if (!container) return;

  if (activeHitTextTimeout !== null) {
    window.clearTimeout(activeHitTextTimeout);
    activeHitTextTimeout = null;
  }
  gsap.killTweensOf(container);

  container.textContent = text;
  container.className = `${HIT_TEXT_BASE_CLASS} ${textClass}`;

  gsap.fromTo(
    container,
    {
      opacity: 0,
      y: -30,
    },
    {
      opacity: 1,
      y: 0,
      duration: 0.1,
      ease: 'power2.out',
    }
  );

  activeHitTextTimeout = window.setTimeout(() => {
    gsap.to(container, {
      opacity: 0,
      y: -10,
      duration: 0.1,
      ease: 'power2.out',
      onComplete: () => {
        container.textContent = '';
        container.className = HIT_TEXT_BASE_CLASS;
        activeHitTextTimeout = null;
      },
    });
  }, 300);
};

watch(gameEngine.hitAnimationTrigger, () => {
  const status = gameEngine.lastHitStatus.value;
  if (!status || !targetLine.value) return;

  let fireworkClass = '';
  let text = '';
  let textClass = '';

  switch (status) {
    case HitStatus.Perfect:
      fireworkClass = 'perfect-hit';
      text = 'PERFECT!';
      textClass = 'perfect-text';
      targetLine.value.style.background = `linear-gradient(to bottom, ${perfectParticleColors.join(', ')})`;
      targetLine.value.style.borderColor = perfectParticleColors[1];
      break;
    case HitStatus.Good:
      fireworkClass = 'good-hit';
      text = 'GOOD!';
      textClass = 'good-text';
      targetLine.value.style.background = primaryColor;
      targetLine.value.style.borderColor = primaryColor;
      break;
    case HitStatus.Missed:
      targetLine.value.style.background = 'gray';
      targetLine.value.style.borderColor = 'gray';
      break;
    default:
      break;
  }

  if (status === HitStatus.Missed) return;

  showHitText(text, textClass);

  // Firework
  // for (let i = 0; i < 10; i++) {
  //   const particle = document.createElement('div');
  //   particle.classList.add('particle');
  //   targetLine.value.appendChild(particle);
  //   particle.style.background = status == HitStatus.Perfect ? perfectParticleColors[Math.floor(Math.random() * perfectParticleColors.length)] : styles.getPropertyValue("--color-primary");
  //   const angle = Math.random() * Math.PI + Math.PI/2; // Random angle in radians
  //   const distance = Math.random() * 100 + 25; // Random distance from the line
  //   const x = Math.cos(angle) * distance;
  //   const y = Math.sin(angle) * distance;

  //   // Animate the particles
  //   gsap.fromTo(
  //     particle,
  //     {
  //       x: 0,
  //       y: 0,
  //       scale: 0.5,
  //       opacity: 1,
  //       top: `${Math.round(Math.random()) * 100}%`,
  //       left: '50%'
  //     },
  //     {
  //       x: x,
  //       y: y,
  //       scale: Math.random() * 1.5 + 0.5,
  //       opacity: 0,
  //       duration: Math.random() * 1 + 0.5,
  //       ease: 'power2.out',
  //       onComplete: () => particle.remove() // Remove the particle after animation
  //     }
  //   );
  // }
  
  // // Add a new firework with a unique ID
  // const fireworkId = nextFireworkId++;
  // fireworks.value.push({ id: fireworkId, class: fireworkClass });
  
  // // Remove the firework after animation completes
  // setTimeout(() => {
  //   fireworks.value = fireworks.value.filter(fw => fw.id !== fireworkId);
  // }, fireworkAnimationDuration);
});
</script>

<template>
  <div class="relative h-full w-full">
    <!--
      Reserved HUD band, multi-line mode only — the score genuinely never
      scrolls the active row into it (see MULTI_HUD_GUTTER_PX), so this is a
      visual cue for the space, not what's hiding the notation underneath.
      Non-interactive so it never blocks the click-to-seek hit test below it.
    -->
    <div
      v-if="isMultiLineMode"
      class="multi-hud-gutter pointer-events-none absolute inset-x-0 top-0 z-5"
      :style="{ height: `${MULTI_HUD_VISUAL_FADE_PX}px` }"
    >
      <!--
        backdrop-filter has one fixed blur radius per element — it can't
        express a spatial gradient on its own. Stacking layers with
        decreasing blur radii, each faded in/out over a wider, overlapping
        band via mask-image approximates a gradually increasing blur (strong
        at the very top, tapering smoothly to fully clear) instead of a
        cutoff. The band is taller than the logical gutter reserved by
        centering (MULTI_HUD_GUTTER_PX) purely so the fade has room to finish
        gradually rather than ending abruptly at that boundary.
      -->
      <div class="multi-hud-gutter__layer multi-hud-gutter__layer--1" />
      <div class="multi-hud-gutter__layer multi-hud-gutter__layer--2" />
      <div class="multi-hud-gutter__layer multi-hud-gutter__layer--3" />
      <div class="multi-hud-gutter__layer multi-hud-gutter__layer--4" />
      <div class="multi-hud-gutter__layer multi-hud-gutter__layer--5" />
    </div>

    <div
      id="hit-text-container"
      class="hit-text-container top-6 left-70"
    />

    <div
      class="sheet-render-mode-button chrome-fade"
      :class="{ 'chrome-fade--hidden': !chrome.visible.value }"
    >
      <ToolbarIconToggle
        :aria-label="modeButtonLabel"
        :title="modeButtonLabel"
        :disabled="isSwitchingRenderMode"
        @click="toggleRenderMode"
      >
        <UIcon
          :name="modeIconName"
          class="w-5 h-5"
        />
      </ToolbarIconToggle>
    </div>

    <div
      v-if="showLoadingOverlay"
      class="absolute inset-0 bg-background/30 backdrop-blur-sm flex items-center justify-center rounded-xl z-30"
    >
      <div class="text-center flex flex-col items-center">
        <div class="w-12 h-12 border-4 border-t-primary border-surface rounded-full animate-spin mb-4" />
        <p class="text-lg font-medium">
          {{ loadingLabel }}
        </p>
      </div>
    </div>

    <div
      ref="sheetViewportEl"
      class="sheet-music-container relative h-full w-full"
      :class="[
        { 'absolute opacity-0': !scrollReady },
        sheetViewportOverflowClass,
        !isMultiLineMode && props.canSeek ? 'cursor-ew-resize select-none' : 'select-none',
      ]"
      :style="{ touchAction: sheetViewportTouchAction }"
      @mousedown="handleSheetPressStart"
      @touchstart="handleSheetPressStart"
      @pointerdown="isMultiLineMode ? handleMultiPointerDown($event) : undefined"
      @pointerup="isMultiLineMode ? handleMultiPointerUp() : undefined"
      @wheel.passive="isMultiLineMode ? handleMultiWheel() : undefined"
      @scroll="isMultiLineMode ? handleMultiScroll() : undefined"
      @click="isMultiLineMode ? handleMultiMeasureClick($event) : undefined"
    >
      <div
        class="sheet-music-stage relative"
        :class="isMultiLineMode ? 'min-h-full' : 'top-1/2 -translate-y-1/2'"
        :style="isMultiLineMode ? undefined : {
          transformOrigin: `${targetPosition}px 50%`,
          transform: `scale(${singleScale})`,
        }"
      >
        <div
          v-if="isMultiLineMode"
          class="multi-top-spacer pointer-events-none"
          :style="{ height: `${multiSpacerPx}px` }"
        />

        <div
          ref="sheetMusicDiv"
          class="sheet-music relative top-0 left-0 right-0"
          :style="isMultiLineMode ? undefined : { transform: `translateX(${scrollOffset}px)` }"
        />

        <SheetMusicSectionsOverlay
          :sections="props.sections ?? []"
          :measure-bounds="measureBoundsForScroll"
          :scroll-offset="isMultiLineMode ? 0 : scrollOffset"
          :scale="isMultiLineMode ? 1 : singleScale"
          :origin-x="targetPosition"
          :mode="isMultiLineMode ? 'multi' : 'single'"
          :show-strips="false"
          :vertical-offset="isMultiLineMode ? multiSpacerPx : 0"
        />

        <SheetMusicAnnotationsOverlay
          v-if="noteAnnotations"
          :notes="gameEngine.currentGameNotes.value"
          :measure-bounds="measureBoundsForScroll"
          :scroll-offset="isMultiLineMode ? 0 : scrollOffset"
          :scale="isMultiLineMode ? 1 : singleScale"
          :vertical-offset="isMultiLineMode ? multiSpacerPx : 0"
          :text-scale="isMultiLineMode ? multiScale : 1"
        />

        <div
          v-if="!isMultiLineMode && sheetMusic.isLoaded.value && gameEngine.targetLineVisible.value"
          ref="targetLine"
          class="target-line top-2 absolute w-0.5 h-full"
          :style="{ left: `${targetPosition}px` }"
        >
          <div class="target-line-top" />
          <div class="target-line-bottom" />
        </div>

        <div
          v-if="isMultiLineMode"
          class="multi-bottom-spacer pointer-events-none"
          :style="{ height: `${multiSpacerPx}px` }"
        />
      </div>

      <div
        v-if="showMultiMarker"
        class="pointer-events-none absolute inset-0 z-1"
      >
        <div
          ref="targetLine"
          class="target-line absolute"
          :style="multiMarkerStyle"
        >
          <div
            class="target-line-top"
            :style="multiMarkerArrowTopStyle"
          />
          <div
            class="target-line-bottom"
            :style="multiMarkerArrowBottomStyle"
          />
        </div>
      </div>

      <div
        v-if="isDev"
        class="pointer-events-none absolute inset-0 z-20 debug-measure-bounds"
      >
        <div
          v-for="(bound, index) in measureBoundsForScroll"
          :key="`debug-bound-${bound.measureIndex}`"
          class="absolute"
          :style="debugBoundStyle(bound, index)"
        />
      </div>
    </div>
  </div>
</template>

<style>
.multi-hud-gutter {
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--color-background) 88%, transparent) 0%,
    color-mix(in srgb, var(--color-background) 45%, transparent) 45%,
    color-mix(in srgb, var(--color-background) 15%, transparent) 75%,
    transparent 100%
  );
}

.multi-hud-gutter__layer {
  position: absolute;
  inset: 0;
}

/*
 * Five layers, each a bit less blurred than the last and masked to a band
 * that heavily overlaps its neighbours' — the overlap is what keeps the
 * transition from one blur radius to the next looking continuous instead of
 * banded/stepped.
 */
.multi-hud-gutter__layer--1 {
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  mask-image: linear-gradient(to bottom, black 0%, black 15%, transparent 40%);
  -webkit-mask-image: linear-gradient(to bottom, black 0%, black 15%, transparent 40%);
}

.multi-hud-gutter__layer--2 {
  backdrop-filter: blur(9px);
  -webkit-backdrop-filter: blur(9px);
  mask-image: linear-gradient(to bottom, transparent 5%, black 25%, black 40%, transparent 60%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 5%, black 25%, black 40%, transparent 60%);
}

.multi-hud-gutter__layer--3 {
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  mask-image: linear-gradient(to bottom, transparent 25%, black 45%, black 60%, transparent 78%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 25%, black 45%, black 60%, transparent 78%);
}

.multi-hud-gutter__layer--4 {
  backdrop-filter: blur(2.5px);
  -webkit-backdrop-filter: blur(2.5px);
  mask-image: linear-gradient(to bottom, transparent 45%, black 65%, black 80%, transparent 92%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 45%, black 65%, black 80%, transparent 92%);
}

.multi-hud-gutter__layer--5 {
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
  mask-image: linear-gradient(to bottom, transparent 65%, black 85%, black 92%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 65%, black 85%, black 92%, transparent 100%);
}


.hit-text-container {
  position: absolute;
  font-size: 24px;
  font-weight: bold;
  width: 150px;
  text-align: center;
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 6;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.8);
}

.perfect-text {
  color: var(--color-hit-perfect);
  text-shadow: 0 0 10px color-mix(in srgb, var(--color-hit-perfect) 70%, transparent);
}

.good-text {
  color: var(--color-hit-good);
  text-shadow: 0 0 10px color-mix(in srgb, var(--color-hit-good) 70%, transparent);
}

.sheet-render-mode-button {
  position: absolute;
  top: 120px;
  right: 16px;
  z-index: 24;
}

.chrome-fade {
  transition: opacity 0.25s ease;
}

.chrome-fade--hidden {
  opacity: 0;
  pointer-events: none;
}

.firework-container {
  position: absolute;
  width: 300px;
  height: 300px;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 5;
}

.firework-container::after {
  content: '';
  position: absolute;
  bottom: 30%;
  left: 50%;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  transform: translate(-50%, 50%);
}

.firework-container::after {
  animation-delay: 0.1s;
}

.perfect-hit::after {
  background-color: var(--color-hit-perfect);
  box-shadow: 0 0 20px 10px color-mix(in srgb, var(--color-hit-perfect) 60%, transparent);
  animation: explode 0.8s ease-out forwards, particles-green 0.8s ease-out forwards;
}

.good-hit::after {
  background-color: var(--color-hit-good);
  box-shadow: 0 0 20px 10px color-mix(in srgb, var(--color-hit-good) 60%, transparent);
  animation: explode 0.8s ease-out forwards, particles-orange 0.8s ease-out forwards;
}

.missed-hit::after {
  background-color: var(--color-hit-missed);
  box-shadow: 0 0 20px 10px color-mix(in srgb, var(--color-hit-missed) 60%, transparent);
  animation: explode 0.8s ease-out forwards, particles-red 0.8s ease-out forwards;
}

@keyframes explode {
  0% {
    width: 10px;
    height: 10px;
    opacity: 1;
  }
  100% {
    width: 300px;
    height: 300px;
    opacity: 0;
  }
}

@keyframes particles-green {
  0% {
    box-shadow:
      0 0 0 0 rgba(76, 175, 80, 0.8),
      0 0 0 0 rgba(76, 175, 80, 0.8),
      0 0 0 0 rgba(76, 175, 80, 0.8),
      0 0 0 0 rgba(76, 175, 80, 0.8),
      0 0 0 0 rgba(76, 175, 80, 0.8),
      0 0 0 0 rgba(76, 175, 80, 0.8),
      0 0 0 0 rgba(76, 175, 80, 0.8),
      0 0 0 0 rgba(76, 175, 80, 0.8);
  }
  100% {
    box-shadow:
      70px -40px 0 -3px rgba(76, 175, 80, 0.8),
      -70px -40px 0 -3px rgba(76, 175, 80, 0.8),
      70px 40px 0 -3px rgba(76, 175, 80, 0.8),
      -70px 40px 0 -3px rgba(76, 175, 80, 0.8),
      40px -70px 0 -3px rgba(76, 175, 80, 0.8),
      -40px -70px 0 -3px rgba(76, 175, 80, 0.8),
      40px 70px 0 -3px rgba(76, 175, 80, 0.8),
      -40px 70px 0 -3px rgba(76, 175, 80, 0.8);
  }
}

@keyframes particles-orange {
  0% {
    box-shadow:
      0 0 0 0 rgba(255, 152, 0, 0.8),
      0 0 0 0 rgba(255, 152, 0, 0.8),
      0 0 0 0 rgba(255, 152, 0, 0.8),
      0 0 0 0 rgba(255, 152, 0, 0.8),
      0 0 0 0 rgba(255, 152, 0, 0.8),
      0 0 0 0 rgba(255, 152, 0, 0.8),
      0 0 0 0 rgba(255, 152, 0, 0.8),
      0 0 0 0 rgba(255, 152, 0, 0.8);
  }
  100% {
    box-shadow:
      70px -40px 0 -3px rgba(255, 152, 0, 0.8),
      -70px -40px 0 -3px rgba(255, 152, 0, 0.8),
      70px 40px 0 -3px rgba(255, 152, 0, 0.8),
      -70px 40px 0 -3px rgba(255, 152, 0, 0.8),
      40px -70px 0 -3px rgba(255, 152, 0, 0.8),
      -40px -70px 0 -3px rgba(255, 152, 0, 0.8),
      40px 70px 0 -3px rgba(255, 152, 0, 0.8),
      -40px 70px 0 -3px rgba(255, 152, 0, 0.8);
  }
}

@keyframes particles-red {
  0% {
    box-shadow:
      0 0 0 0 rgba(244, 67, 54, 0.8),
      0 0 0 0 rgba(244, 67, 54, 0.8),
      0 0 0 0 rgba(244, 67, 54, 0.8),
      0 0 0 0 rgba(244, 67, 54, 0.8),
      0 0 0 0 rgba(244, 67, 54, 0.8),
      0 0 0 0 rgba(244, 67, 54, 0.8),
      0 0 0 0 rgba(244, 67, 54, 0.8),
      0 0 0 0 rgba(244, 67, 54, 0.8);
  }
  100% {
    box-shadow:
      70px -40px 0 -3px rgba(244, 67, 54, 0.8),
      -70px -40px 0 -3px rgba(244, 67, 54, 0.8),
      70px 40px 0 -3px rgba(244, 67, 54, 0.8),
      -70px 40px 0 -3px rgba(244, 67, 54, 0.8),
      40px -70px 0 -3px rgba(244, 67, 54, 0.8),
      -40px -70px 0 -3px rgba(244, 67, 54, 0.8),
      40px 70px 0 -3px rgba(244, 67, 54, 0.8),
      -40px 70px 0 -3px rgba(244, 67, 54, 0.8);
  }
}

.particle {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  opacity: 0;
}

.target-line {
  background-color: var(--color-text-primary);
  border-color: var(--color-text-primary);
}

.target-line-top {
  position: absolute;
  top: 0;
  left: -7px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 12px solid transparent;
  border-top-color: inherit;
}

.target-line-bottom {
  position: absolute;
  bottom: 0;
  left: -7px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 12px solid transparent;
  border-bottom-color: inherit;
}
</style>
